import React, { useState, useEffect } from 'react';
import { updateTeamSchedule } from '../utils/clubs';
import '../styles/TeamScheduleEditor.css';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

function TeamScheduleEditor({ clubId, team, existingSchedule, onSaved }) {
  const [practiceDays, setPracticeDays] = useState([]);
  const [schoolGameDays, setSchoolGameDays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (existingSchedule) {
      setPracticeDays(existingSchedule.practiceDays || []);
      setSchoolGameDays(existingSchedule.schoolGameDays || []);
    }
  }, [existingSchedule]);

  const togglePracticeDay = (day) => {
    setPracticeDays((prev) => {
      const existing = prev.find((d) => d.day === day);
      if (existing) return prev.filter((d) => d.day !== day);
      return [...prev, { day, startTime: '17:00', endTime: '19:00' }];
    });
    setSaved(false);
  };

  const updateTime = (day, field, value) => {
    setPracticeDays((prev) => prev.map((d) => d.day === day ? { ...d, [field]: value } : d));
    setSaved(false);
  };

  const toggleSchoolGameDay = (day) => {
    setSchoolGameDays((prev) => {
      if (prev.includes(day)) return prev.filter((d) => d !== day);
      return [...prev, day];
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTeamSchedule(clubId, team.id, {
        ageGroup: team.ageGroup,
        label: team.label,
        practiceDays,
        schoolPracticeDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        clubGameDays: ['saturday', 'sunday'],
        schoolGameDays,
        season: 'club',
      });
      setSaved(true);
      if (onSaved) onSaved();
    } catch (err) {
      console.error('[TeamScheduleEditor] Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tse-card">
      <h4 className="tse-header">{team.ageGroup} &mdash; {team.label}</h4>

      {/* Club Practice Days */}
      <div className="tse-section">
        <label className="tse-label">Club Practice Days</label>
        <div className="tse-day-grid">
          {DAYS.map((day) => {
            const active = practiceDays.find((d) => d.day === day);
            return (
              <button
                key={day}
                type="button"
                className={`tse-day-chip ${active ? 'active' : ''}`}
                onClick={() => togglePracticeDay(day)}
              >
                {DAY_LABELS[day]}
              </button>
            );
          })}
        </div>
        {practiceDays.map((pd) => (
          <div key={pd.day} className="tse-time-row">
            <span className="tse-time-label">{DAY_LABELS[pd.day]}</span>
            <input
              type="time"
              className="tse-time-input"
              value={pd.startTime}
              onChange={(e) => updateTime(pd.day, 'startTime', e.target.value)}
            />
            <span className="tse-time-separator">to</span>
            <input
              type="time"
              className="tse-time-input"
              value={pd.endTime}
              onChange={(e) => updateTime(pd.day, 'endTime', e.target.value)}
            />
          </div>
        ))}
      </div>

      {/* School Practice Days */}
      <div className="tse-section">
        <label className="tse-label">School Practice Days</label>
        <div className="tse-day-grid">
          {WEEKDAYS.map((day) => (
            <button
              key={day}
              type="button"
              className="tse-day-chip active"
              disabled
            >
              {DAY_LABELS[day]}
            </button>
          ))}
        </div>
        <p className="tse-auto-days">Mon–Fri — school practice is every weekday</p>
      </div>

      {/* Club Game Days */}
      <div className="tse-section">
        <label className="tse-label">Game Days — Club In-Season</label>
        <div className="tse-day-grid">
          {['saturday', 'sunday'].map((day) => (
            <button
              key={day}
              type="button"
              className="tse-day-chip game active"
              disabled
            >
              {DAY_LABELS[day]}
            </button>
          ))}
        </div>
        <p className="tse-auto-days">Sat &amp; Sun — tournaments are always weekends</p>
      </div>

      {/* School Game Days */}
      <div className="tse-section">
        <label className="tse-label">Game Days — School In-Season <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>(optional)</span></label>
        <div className="tse-day-grid">
          {WEEKDAYS.map((day) => (
            <button
              key={day}
              type="button"
              className={`tse-day-chip game ${schoolGameDays.includes(day) ? 'active' : ''}`}
              onClick={() => toggleSchoolGameDay(day)}
            >
              {DAY_LABELS[day]}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        className={`tse-save-btn ${saved ? 'saved' : ''}`}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : saved ? 'Saved' : 'Save Schedule'}
      </button>
    </div>
  );
}

export default TeamScheduleEditor;
